import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { getDefaultThumbnail } from "@/lib/strapi";

const SEARCH_TABS = [
  { value: "sports", label: "Sport" },
  { value: "travel", label: "Travel" },
  { value: "interviews", label: "Interviews" },
  { value: "clubs", label: "Club" },
  { value: "meets", label: "Meet" },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

export function SearchDialog({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("sports");
  const inputRef = useRef(null);
  const fallbackThumbnail = getDefaultThumbnail();
  const { results, isLoading, error, hasSearched } = useGlobalSearch(query, { enabled: open });

  const activeResults = results[activeTab] || [];

  useEffect(() => {
    if (open) {
      window.requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      return;
    }

    setQuery("");
    setActiveTab("sports");
  }, [open]);

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  const resultCounts = useMemo(
    () => ({
      sports: results.sports.length,
      travel: results.travel.length,
      interviews: results.interviews.length,
      clubs: results.clubs.length,
      meets: results.meets.length,
    }),
    [results]
  );

  const queryReady = query.trim().length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!left-0 !top-0 !h-[100dvh] !w-[100dvw] !max-w-none !translate-x-0 !translate-y-0 !rounded-none !border-0 !bg-amber-pale !p-0 !shadow-none overflow-hidden">
        <div className="flex h-full flex-col">
          <div className="border-b border-near-black/10 bg-white/90 px-6 py-6 shadow-[0_1px_0_rgba(186,117,23,0.06)] backdrop-blur">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-5 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="mb-2 font-inter text-xs font-light uppercase tracking-[0.25em] text-warm-gray">
                  Search the site
                </p>
                <h2 className="font-inter text-2xl font-semibold tracking-tight text-near-black md:text-4xl">
                  Find stories, clubs and events
                </h2>
                <p className="mt-2 max-w-2xl font-inter text-sm text-warm-gray md:text-base">
                  Search across sports, travel, interviews, clubs and meets. Results update automatically as you type.
                </p>
              </div>
              <div className="relative w-full md:max-w-xl">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-warm-gray" />
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by keyword, name, place or title"
                  className="h-12 rounded-full border-near-black/10 bg-amber-pale pl-11 pr-4 font-inter text-base text-near-black shadow-none placeholder:text-warm-gray focus-visible:ring-2 focus-visible:ring-solar-orange"
                  aria-label="Search site content"
                />
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mx-auto w-full max-w-7xl flex-1 px-6 py-6 md:py-8">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full min-h-0 flex-col">
                <TabsList className="mb-6 flex h-auto flex-wrap gap-2 rounded-none bg-transparent p-0">
                  {SEARCH_TABS.map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="inline-flex items-center gap-2 rounded-full border border-near-black/10 bg-white px-4 py-2 font-inter text-sm font-medium text-near-black shadow-sm data-[state=active]:border-solar-orange data-[state=active]:bg-solar-orange data-[state=active]:text-near-black"
                    >
                      <span>{tab.label}</span>
                      <span className="rounded-full bg-black/5 px-2 py-0.5 text-[11px] font-semibold leading-none text-warm-gray data-[state=active]:bg-black/10 data-[state=active]:text-near-black">
                        {resultCounts[tab.value]}
                      </span>
                    </TabsTrigger>
                  ))}
                </TabsList>

                <div className="min-h-0 flex-1 overflow-hidden rounded-3xl border border-near-black/10 bg-white shadow-[0_20px_80px_rgba(29,29,27,0.06)]">
                  <ScrollArea className="h-full">
                    <div className="p-5 md:p-6">
                      {!queryReady && (
                        <div className="flex min-h-[45vh] items-center justify-center rounded-2xl border border-dashed border-near-black/10 bg-amber-pale px-6 text-center">
                          <div className="max-w-lg">
                            <p className="font-inter text-xs font-light uppercase tracking-[0.25em] text-warm-gray">
                              Start typing
                            </p>
                            <h3 className="mt-3 font-inter text-2xl font-semibold tracking-tight text-near-black">
                              Search the whole site from one place
                            </h3>
                            <p className="mt-3 font-inter text-sm leading-relaxed text-warm-gray">
                              Enter at least two characters to load matching results in each tab.
                            </p>
                          </div>
                        </div>
                      )}

                      {!!error && queryReady && (
                        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
                          Unable to search right now. Please try again shortly.
                        </div>
                      )}

                      {queryReady && !error && isLoading && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                            {Array.from({ length: 3 }).map((_, index) => (
                              <div key={`search-skeleton-${index}`} className="flex gap-4 rounded-2xl border border-near-black/10 p-4">
                                <Skeleton className="h-24 w-24 rounded-xl" />
                                <div className="flex-1 space-y-3 py-1">
                                  <Skeleton className="h-3 w-28" />
                                  <Skeleton className="h-5 w-11/12" />
                                  <Skeleton className="h-4 w-full" />
                                  <Skeleton className="h-4 w-5/6" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {queryReady && !error && !isLoading && (
                        <TabsContent value={activeTab} className="mt-0 focus-visible:outline-none">
                          {activeResults.length > 0 ? (
                            <motion.div
                              variants={containerVariants}
                              initial="hidden"
                              animate="visible"
                              className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3"
                            >
                              {activeResults.map((item) => (
                                <motion.div key={`${activeTab}-${item.slug || item.id}`} variants={itemVariants}>
                                  <Link
                                    to={item.href}
                                    onClick={() => onOpenChange(false)}
                                    className="group flex h-full gap-4 rounded-2xl border border-near-black/10 bg-amber-pale p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-solar-orange/40 hover:shadow-[0_16px_40px_rgba(29,29,27,0.08)]"
                                  >
                                    <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-warm-gray/10">
                                      <img
                                        src={item.image}
                                        alt={item.title}
                                        loading="lazy"
                                        decoding="async"
                                        width={400}
                                        height={400}
                                        onError={handleImageError}
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                      />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-warm-gray">
                                        <span>{item.meta}</span>
                                        {item.date && <span>• {item.date}</span>}
                                      </div>
                                      <h3 className="mt-2 line-clamp-2 font-inter text-lg font-semibold tracking-tight text-near-black group-hover:text-amber-dusk">
                                        {item.title}
                                      </h3>
                                      <p className="mt-2 line-clamp-2 font-inter text-sm leading-relaxed text-warm-gray">
                                        {item.teaser}
                                      </p>
                                      <span className="mt-4 inline-flex items-center gap-1 font-inter text-sm font-medium text-amber-dusk transition-all duration-150 group-hover:gap-2">
                                        Read more
                                        <ArrowUpRight size={14} />
                                      </span>
                                    </div>
                                  </Link>
                                </motion.div>
                              ))}
                            </motion.div>
                          ) : (
                            <div className="flex min-h-[45vh] items-center justify-center rounded-2xl border border-dashed border-near-black/10 bg-amber-pale px-6 text-center">
                              <div className="max-w-lg">
                                <p className="font-inter text-xs font-light uppercase tracking-[0.25em] text-warm-gray">
                                  No results
                                </p>
                                <h3 className="mt-3 font-inter text-2xl font-semibold tracking-tight text-near-black">
                                  No {SEARCH_TABS.find((tab) => tab.value === activeTab)?.label.toLowerCase()} matches found
                                </h3>
                                <p className="mt-3 font-inter text-sm leading-relaxed text-warm-gray">
                                  Try a different keyword or switch tabs to see results from another section.
                                </p>
                              </div>
                            </div>
                          )}
                        </TabsContent>
                      )}

                      {queryReady && !isLoading && !error && !hasSearched && (
                        <div className="rounded-2xl border border-dashed border-near-black/10 bg-amber-pale px-6 py-10 text-center text-warm-gray">
                          Type to search across the site.
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </Tabs>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}